<template>
  <div class="rv2" :class="{ 'is-busy': busy, 'has-sheet': !!(sheet || pending), 'is-ready': ready, 'has-clip': !!weekClipboard }">
    <div v-if="busy" class="rv2-progress" aria-hidden="true"><i /></div>

    <header class="rv2-top rv2-top-desktop">
      <div class="rv2-hero">
        <button class="rv2-chev" type="button" aria-label="Previous week" @click="shiftWeek(-7)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="rv2-hero-copy">
          <div class="rv2-kicker">
            <span>{{ shopName }}</span>
            <i>Beta</i>
          </div>
          <transition :name="weekMotion" mode="out-in">
            <h1 :key="weekDisplay" class="rv2-title">{{ weekDisplay }}</h1>
          </transition>
          <transition name="rv2-fade" mode="out-in">
            <p :key="insightText" class="rv2-sub">{{ insightText }}</p>
          </transition>
        </div>
        <button class="rv2-chev" type="button" aria-label="Next week" @click="shiftWeek(7)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9.5 5.5L16 12l-6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <div class="rv2-toolbar">
        <button
          v-if="!isThisWeek"
          class="rv2-chip-btn"
          type="button"
          @click="goThisWeek"
        >
          This week
        </button>
        <button class="rv2-chip-btn" type="button" :disabled="busy || !staff.length" @click="copyThisWeek">
          Copy week
        </button>
        <button
          class="rv2-chip-btn"
          type="button"
          :disabled="pasteDisabled"
          @click="pasteWeek"
        >
          Paste week
        </button>
        <button class="rv2-chip-btn" type="button" :disabled="busy || !staff.length" @click="copyLastWeek">
          Use week before
        </button>
        <button class="rv2-chip-btn" type="button" :disabled="busy || !staff.length" @click="fillDefaults">
          Use usual hours
        </button>
        <button
          class="rv2-chip-btn is-danger"
          type="button"
          :disabled="busy || !staff.length"
          @click="clearWeek"
        >
          Clear week
        </button>
        <div class="rv2-toolbar-end">
          <div class="rv2-mode" role="tablist" aria-label="Roster view">
            <button
              type="button"
              class="rv2-mode-btn"
              :class="{ 'is-on': mode === 'week' }"
              @click="setMode('week')"
            >
              Week
            </button>
            <button
              type="button"
              class="rv2-mode-btn"
              :class="{ 'is-on': mode === 'day' }"
              @click="setMode('day')"
            >
              Day
            </button>
          </div>
          <router-link class="rv2-classic" to="/management/roster">Classic calendar</router-link>
        </div>
      </div>
    </header>

    <header class="rv2-phone">
      <div class="rv2-phone-week">
        <div class="rv2-phone-week-copy">
          <span>{{ shopName }}</span>
          <transition :name="weekMotion" mode="out-in">
            <strong :key="weekDisplay">{{ weekDisplay }}</strong>
          </transition>
        </div>
        <div v-click-outside="closeMore" class="rv2-more">
          <button
            class="rv2-more-btn"
            type="button"
            aria-label="Week actions"
            :aria-expanded="moreOpen ? 'true' : 'false'"
            @click="moreOpen = !moreOpen"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="6" cy="12" r="1.7" fill="currentColor" />
              <circle cx="12" cy="12" r="1.7" fill="currentColor" />
              <circle cx="18" cy="12" r="1.7" fill="currentColor" />
            </svg>
          </button>
          <transition name="rv2-pop">
          <div v-if="moreOpen" class="rv2-more-menu" role="menu">
            <button
              v-if="!isThisWeek || selectedDate !== today"
              type="button"
              role="menuitem"
              @click="jumpToday"
            >
              Jump to today
            </button>
            <button type="button" role="menuitem" :disabled="busy || !staff.length" @click="runCopy">
              Use week before
            </button>
            <button type="button" role="menuitem" :disabled="busy || !staff.length" @click="runFill">
              Use usual hours
            </button>
            <button
              type="button"
              role="menuitem"
              class="is-danger"
              :disabled="busy || !staff.length"
              @click="runClear"
            >
              Clear week
            </button>
            <router-link to="/management/roster" role="menuitem" @click.native="closeMore">
              Classic calendar
            </router-link>
          </div>
          </transition>
        </div>
      </div>
    </header>

    <div v-if="weekClipboard" class="rv2-clip">
      <p>
        Copied <strong>{{ weekClipboard.weekDisplay }}</strong>.
        {{ weekClipboard.copied }}
        {{ weekClipboard.copied === 1 ? "day" : "days" }}.
        Go to another week to paste.
      </p>
      <div class="rv2-clip-actions">
        <button
          class="rv2-chip-btn"
          type="button"
          :disabled="pasteDisabled"
          @click="pasteWeek"
        >
          {{ isClipboardWeek ? "This is the copied week" : "Paste here" }}
        </button>
        <button class="rv2-text-btn" type="button" @click="clearClipboard">Clear</button>
      </div>
    </div>

    <transition name="rv2-overlay">
    <div v-if="pending" class="rv2-scrim is-front" @click.self="closePending">
      <div
        class="rv2-sheet rv2-preview"
        role="dialog"
        aria-modal="true"
        :aria-label="pending.title"
      >
        <button
          type="button"
          class="rv2-sheet-handle"
          aria-label="Close"
          @click="closePending"
        >
          <i />
        </button>
        <p class="rv2-sheet-kicker">{{ pending.kicker }}</p>
        <h2>{{ pending.title }}</h2>
        <p class="rv2-preview-lead" :class="{ 'is-warn': pending.danger }">{{ pending.body }}</p>
        <p v-if="pending.note" class="rv2-preview-note">{{ pending.note }}</p>
        <p v-if="pending.skipped" class="rv2-preview-note">
          Skipping {{ pending.skipped }}
          {{ pending.skipped === 1 ? "day" : "days" }} that already
          {{ pending.skipped === 1 ? "has" : "have" }} hours.
        </p>
        <div class="rv2-preview-list">
          <div
            v-for="group in pendingGroups"
            :key="group.resourceId"
            class="rv2-preview-group"
          >
            <strong>{{ group.name }}</strong>
            <ul>
              <li v-for="row in group.rows" :key="row.date">
                <span>{{ row.dayLabel }}</span>
                <b :class="'is-' + row.kind">{{ row.label }}</b>
              </li>
            </ul>
          </div>
        </div>
        <div class="rv2-preview-actions">
          <button class="rv2-text-btn" type="button" @click="closePending">Cancel</button>
          <button
            class="rv2-primary"
            :class="{ 'is-danger': pending.danger }"
            type="button"
            :disabled="busy"
            @click="confirmPending"
          >
            {{ pending.action }}
          </button>
        </div>
      </div>
    </div>
    </transition>

    <div v-if="loading" class="rv2-skeleton" aria-hidden="true">
      <div class="rv2-metrics">
        <div v-for="n in 3" :key="'sm-' + n" class="rv2-metric is-wait" />
      </div>
      <div class="rv2-skel-board">
        <div v-for="n in 24" :key="'sc-' + n" class="rv2-skel-cell" />
      </div>
    </div>

    <div v-else-if="!staff.length" class="rv2-empty">
      <p class="rv2-empty-kicker">No staff</p>
      <h2>Add people first.</h2>
      <p>Add staff in Employees, then come back to set their hours.</p>
      <router-link class="rv2-primary" to="/management/employees">Add staff</router-link>
    </div>

    <template v-else>
      <section class="rv2-metrics" aria-label="Week summary">
        <article v-for="metric in metrics" :key="metric.label" class="rv2-metric" :class="{ 'is-warn': metric.warn }">
          <strong>{{ metric.value }}</strong>
          <span>{{ metric.label }}</span>
        </article>
      </section>

      <div v-if="mode === 'day'" class="rv2-strip" role="tablist" aria-label="Days this week">
        <button class="rv2-strip-nav" type="button" aria-label="Previous week" @click="shiftWeek(-7)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <button
          v-for="date in week"
          :key="'s-' + date"
          type="button"
          class="rv2-strip-day"
          :class="dayHeadClass(date)"
          @click="openDay(date)"
        >
          <span>{{ shortDay(date) }}</span>
          <b>{{ monthDay(date) }}</b>
          <i v-if="coverage[date].open && !coverage[date].covered" class="rv2-strip-dot" />
        </button>
        <button class="rv2-strip-nav" type="button" aria-label="Next week" @click="shiftWeek(7)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9.5 5.5L16 12l-6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <p v-if="mode === 'day'" class="rv2-phone-status">{{ phoneStatusLine }}</p>

      <section v-if="mode === 'week'" class="rv2-board" aria-label="Week roster">
        <div class="rv2-grid" :style="gridStyle">
          <div class="rv2-corner">
            <span>Staff</span>
            <small>{{ staff.length }}</small>
          </div>
          <button
            v-for="date in week"
            :key="'h-' + date"
            type="button"
            class="rv2-dayhead"
            :class="dayHeadClass(date)"
            @click="openDay(date)"
          >
            <span class="rv2-dow">{{ shortDay(date) }}</span>
            <span class="rv2-dom">{{ monthDay(date) }}</span>
            <span v-if="coverage[date].open && !coverage[date].covered" class="rv2-pill is-gap">Gap</span>
            <span v-else-if="!coverage[date].open" class="rv2-pill is-shut">Shut</span>
            <span v-else class="rv2-pill is-ok">{{ coverage[date].working }}</span>
          </button>

          <template v-for="meta in staffMeta">
            <div :key="'n-' + meta.person._id" class="rv2-name">
              <div class="rv2-face" :style="{ background: meta.color }">
                <img
                  v-if="meta.person.image && !brokenImages[meta.person._id]"
                  :src="meta.person.image"
                  :alt="meta.person.name"
                  @error="markBroken(meta.person._id)"
                />
                <span v-else>{{ meta.initials }}</span>
              </div>
              <div class="rv2-name-copy">
                <strong>{{ meta.person.name }}</strong>
                <span>{{ formatHoursLabel(meta.hours) }}</span>
                <span class="rv2-hours-track" aria-hidden="true">
                  <i :style="{ width: hoursPct(meta.hours) + '%' }" />
                </span>
              </div>
            </div>
            <button
              v-for="date in week"
              :key="meta.person._id + date"
              type="button"
              class="rv2-cell"
              :class="cellClass(meta.person, date)"
              :aria-label="cellAria(meta.person, date)"
              @click="openCell(meta.person, date)"
            >
              <span class="rv2-chip">
                <span class="rv2-cell-label">{{ summaryFor(meta.person._id, date).label }}</span>
                <span v-if="summaryFor(meta.person._id, date).breakLabel" class="rv2-cell-break">
                  Lunch {{ summaryFor(meta.person._id, date).breakLabel }}
                </span>
                <span
                  v-else-if="summaryFor(meta.person._id, date).spanLabel"
                  class="rv2-cell-break"
                >
                  {{ summaryFor(meta.person._id, date).spanLabel }}
                </span>
              </span>
            </button>
          </template>
        </div>
      </section>

      <section v-else class="rv2-dayboard" aria-label="Day roster">
        <div class="rv2-day-hero">
          <button class="rv2-chev" type="button" aria-label="Previous day" @click="shiftDay(-1)">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div>
            <strong>{{ dayTitle }}</strong>
            <span v-if="selectedCoverage.open && !selectedCoverage.covered">No one working</span>
            <span v-else-if="!selectedCoverage.open">Closed</span>
            <span v-else>{{ selectedCoverage.working }} on</span>
          </div>
          <button class="rv2-chev" type="button" aria-label="Next day" @click="shiftDay(1)">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9.5 5.5L16 12l-6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <button
          v-for="meta in staffMeta"
          :key="'d-' + meta.person._id"
          type="button"
          class="rv2-card"
          :class="{
            'is-selected':
              sheet &&
              sheet.person._id === meta.person._id &&
              sheet.date === selectedDate
          }"
          @click="openCell(meta.person, selectedDate)"
        >
          <div class="rv2-card-who">
            <div class="rv2-face" :style="{ background: meta.color }">
              <img
                v-if="meta.person.image && !brokenImages[meta.person._id]"
                :src="meta.person.image"
                :alt="meta.person.name"
                @error="markBroken(meta.person._id)"
              />
              <span v-else>{{ meta.initials }}</span>
            </div>
            <div>
              <strong>{{ meta.person.name }}</strong>
              <span>{{ formatHoursLabel(meta.hours) }} this week</span>
            </div>
          </div>
          <div
            class="rv2-card-shift"
            :class="'is-' + summaryFor(meta.person._id, selectedDate).kind"
          >
            <b>{{ summaryFor(meta.person._id, selectedDate).label }}</b>
            <small v-if="summaryFor(meta.person._id, selectedDate).breakLabel">
              Lunch {{ summaryFor(meta.person._id, selectedDate).breakLabel }}
            </small>
            <small v-else-if="summaryFor(meta.person._id, selectedDate).spanLabel">
              {{ summaryFor(meta.person._id, selectedDate).spanLabel }}
            </small>
          </div>
        </button>
      </section>

      <ul class="rv2-legend" aria-hidden="true">
        <li><i class="is-work" /> On</li>
        <li><i class="is-off" /> Off</li>
        <li><i class="is-sick" /> Sick</li>
        <li><i class="is-holiday" /> Away</li>
      </ul>
    </template>

    <transition name="rv2-overlay">
    <div v-if="sheet" class="rv2-scrim" @click.self="closeSheet">
      <div
        class="rv2-sheet"
        role="dialog"
        aria-modal="true"
        :aria-label="sheet.person.name + ' on ' + sheetDayLabel"
      >
        <button
          type="button"
          class="rv2-sheet-handle"
          aria-label="Close"
          @click="closeSheet"
        >
          <i />
        </button>
        <header class="rv2-sheet-head">
          <div class="rv2-face is-lg" :style="{ background: tone(sheet.person._id) }">
            <img
              v-if="sheet.person.image && !brokenImages[sheet.person._id]"
              :src="sheet.person.image"
              :alt="sheet.person.name"
              @error="markBroken(sheet.person._id)"
            />
            <span v-else>{{ staffInitials(sheet.person.name) }}</span>
          </div>
          <div>
            <p class="rv2-sheet-kicker">{{ sheetDayLabel }}</p>
            <h2>{{ sheet.person.name }}</h2>
            <p class="rv2-sheet-now">Now {{ sheetSummary.label }}</p>
            <p v-if="sheetSummary.classic && sheetSummary.rangeLabel" class="rv2-sheet-span">
              Classic calendar · {{ sheetSummary.rangeLabel }}
            </p>
          </div>
        </header>

        <div class="rv2-choices">
          <button
            type="button"
            class="rv2-choice is-work"
            :disabled="busy || !sheetShopHours"
            @click="setShopHours"
          >
            <strong>Shop hours</strong>
            <span>{{ shopHoursLabel }}</span>
          </button>
          <button
            type="button"
            class="rv2-choice is-off"
            :disabled="busy"
            @click="setOff"
          >
            <strong>Mark off</strong>
            <span>Not working</span>
          </button>
          <button
            type="button"
            class="rv2-choice is-sick"
            :disabled="busy"
            @click="setLeave('RESOURCE_SICK')"
          >
            <strong>Mark sick</strong>
            <span>Out sick</span>
          </button>
          <button
            type="button"
            class="rv2-choice is-holiday"
            :disabled="busy"
            @click="setLeave('RESOURCE_HOLIDAY')"
          >
            <strong>Mark away</strong>
            <span>Holiday or time off</span>
          </button>

          <div class="rv2-custom" :class="{ 'is-open': customOpen }">
            <button
              type="button"
              class="rv2-choice is-custom"
              :class="{ 'is-open': customOpen }"
              :disabled="busy"
              :aria-expanded="customOpen ? 'true' : 'false'"
              aria-controls="rv2-custom-hours"
              @click="toggleCustomHours"
            >
              <strong>Different hours</strong>
              <span>{{ customHoursHint }}</span>
            </button>
            <div
              v-if="customOpen"
              id="rv2-custom-hours"
              class="rv2-custom-form"
            >
              <div class="rv2-times">
                <label>
                  Start
                  <input v-model="sheet.start" type="time" />
                </label>
                <label>
                  End
                  <input v-model="sheet.end" type="time" />
                </label>
              </div>
              <button
                type="button"
                class="rv2-primary rv2-save-hours"
                :class="{ 'is-quiet': !customDirty }"
                :disabled="busy"
                @click="setCustomHours"
              >
                Save hours
              </button>
            </div>
          </div>
        </div>

        <button type="button" class="rv2-close" @click="closeSheet">Close</button>
      </div>
    </div>
    </transition>

    <section
      class="rv2-dock"
      :class="{
        'is-armed': !!weekClipboard,
        'is-here': isClipboardWeek,
        'is-flash': clipFlash
      }"
      aria-label="Copy and paste week"
    >
      <div class="rv2-dock-bar">
        <button
          class="rv2-dock-btn is-copy"
          type="button"
          :disabled="busy || !staff.length"
          @click="copyThisWeek"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="8" y="8" width="12" height="12" rx="2.2" fill="none" stroke="currentColor" stroke-width="1.7" />
            <path d="M16 8V6.2A2.2 2.2 0 0 0 13.8 4H6.2A2.2 2.2 0 0 0 4 6.2v7.6A2.2 2.2 0 0 0 6.2 16H8" fill="none" stroke="currentColor" stroke-width="1.7" />
          </svg>
          <span>{{ copyButtonLabel }}</span>
        </button>
        <button
          class="rv2-dock-btn is-paste"
          type="button"
          :disabled="pasteDisabled"
          @click="pasteWeek"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 4h6v2H9z" fill="currentColor" />
            <rect x="5" y="5" width="14" height="15" rx="2.2" fill="none" stroke="currentColor" stroke-width="1.7" />
            <path d="M9 12h6M9 16h4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
          </svg>
          <span>{{ pasteButtonLabel }}</span>
        </button>
      </div>
      <div class="rv2-dock-reveal" :class="{ 'is-open': !!weekClipboard }">
        <div class="rv2-dock-reveal-inner">
          <p class="rv2-dock-status" aria-live="polite">
            <template v-if="weekClipboard">
              Copied <strong>{{ weekClipboard.weekDisplay }}</strong>.
              {{ weekClipboard.copied }}
              {{ weekClipboard.copied === 1 ? "day" : "days" }}.
            </template>
          </p>
          <p class="rv2-dock-hint">{{ dockHint }}</p>
          <button class="rv2-dock-clear" type="button" @click="clearClipboard">
            Clear
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ColorHash from "color-hash";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import {
  RESOURCE_HOLIDAY,
  RESOURCE_SICK
} from "@/helpers/eventTypes";
import {
  addDaysIso,
  classicSpanConflict,
  dayCoverage,
  eventsForCell,
  formatClassicSpanWarning,
  formatDayShort,
  formatHoursLabel,
  formatInsightLine,
  formatWeekDisplay,
  isWeekendIso,
  mondayOfWeek,
  monthDayLabel,
  planCopyPreviousWeek,
  planClearDay,
  planClearWeek,
  planFillDefaults,
  planPasteWeek,
  planSetLeave,
  planSetWork,
  snapshotWeek,
  spanningOutsideWeek,
  previewRowsFromCreates,
  isValidShiftRange,
  rosterableEmployees,
  shopHoursForDate,
  shortDayLabel,
  staffInitials,
  summarizeCell,
  weekDates,
  weekHoursForResource,
  weekInsight
} from "@/helpers/rosterV2";

const faceColor = new ColorHash({
  hue: { min: 12, max: 340 },
  lightness: [0.36, 0.4, 0.46],
  saturation: [0.2, 0.26, 0.32]
});

export default {
  name: "RosterV2",
  data() {
    const today = moment.utc().format("YYYY-MM-DD");
    return {
      today,
      weekStart: mondayOfWeek(today),
      selectedDate: today,
      mode: this.defaultMode(),
      userChoseMode: false,
      loading: true,
      busy: false,
      sheet: null,
      customOpen: false,
      pending: null,
      moreOpen: false,
      ready: false,
      weekDir: "next",
      weekClipboard: null,
      clipFlash: false,
      clipFlashTimer: null,
      brokenImages: {}
    };
  },
  computed: {
    ...mapGetters({
      roster: "roster/roster",
      employeesList: "employees/employeesList",
      shop: "shops/shop"
    }),
    events() {
      return Object.values(this.roster || {});
    },
    staff() {
      return rosterableEmployees(this.employeesList);
    },
    staffIds() {
      return this.staff.map(person => person._id);
    },
    week() {
      return weekDates(this.weekStart);
    },
    weekDisplay() {
      return formatWeekDisplay(this.weekStart, this.today);
    },
    isThisWeek() {
      return this.weekStart === mondayOfWeek(this.today);
    },
    insight() {
      return weekInsight({
        shop: this.shop,
        events: this.events,
        resourceIds: this.staffIds,
        week: this.week,
        today: this.today
      });
    },
    insightText() {
      return formatInsightLine(this.insight, { isThisWeek: this.isThisWeek });
    },
    metrics() {
      const insight = this.insight;
      return [
        {
          value: this.isThisWeek ? String(insight.workingToday) : String(insight.staffCount),
          label: this.isThisWeek ? "On today" : "Staff",
          warn: false
        },
        {
          value: String(insight.gaps),
          label: "To cover",
          warn: insight.gaps > 0
        },
        {
          value: formatHoursLabel(insight.hours),
          label: "Hours",
          warn: false
        }
      ];
    },
    coverage() {
      const map = {};
      this.week.forEach(date => {
        map[date] = dayCoverage(this.shop, this.events, this.staffIds, date);
      });
      return map;
    },
    board() {
      const map = {};
      this.staff.forEach(person => {
        map[person._id] = {};
        this.week.forEach(date => {
          map[person._id][date] = summarizeCell(
            eventsForCell(this.events, person._id, date),
            date
          );
        });
      });
      return map;
    },
    staffMeta() {
      return this.staff.map(person => ({
        person,
        initials: staffInitials(person.name),
        hours: weekHoursForResource(this.events, person._id, this.week),
        color: this.tone(person._id)
      }));
    },
    selectedCoverage() {
      return (
        this.coverage[this.selectedDate] ||
        dayCoverage(this.shop, this.events, this.staffIds, this.selectedDate)
      );
    },
    dayTitle() {
      return moment.utc(this.selectedDate, "YYYY-MM-DD", true).format("dddd D MMMM");
    },
    gridStyle() {
      return {
        gridTemplateColumns: `minmax(148px, 188px) repeat(${this.week.length}, minmax(88px, 1fr))`
      };
    },
    sheetDayLabel() {
      if (!this.sheet) return "";
      return moment.utc(this.sheet.date, "YYYY-MM-DD", true).format("dddd D MMMM");
    },
    sheetShopHours() {
      if (!this.sheet) return null;
      return shopHoursForDate(this.shop, this.sheet.date);
    },
    shopHoursLabel() {
      if (!this.sheetShopHours) return "Shop is closed";
      return `${this.sheetShopHours.start}–${this.sheetShopHours.end}`;
    },
    shopName() {
      return (this.shop && this.shop.name) || "Roster";
    },
    sheetSummary() {
      if (!this.sheet) return { kind: "off", label: "Off" };
      return (
        (this.board[this.sheet.person._id] &&
          this.board[this.sheet.person._id][this.sheet.date]) ||
        summarizeCell(this.cellEvents())
      );
    },
    isShopHoursShift() {
      const hours = this.sheetShopHours;
      const summary = this.sheetSummary;
      return !!(
        summary.kind === "work" &&
        hours &&
        summary.start === hours.start &&
        summary.end === hours.end
      );
    },
    customDirty() {
      if (!this.sheet) return false;
      return (
        this.sheet.start !== (this.sheetSummary.start || "") ||
        this.sheet.end !== (this.sheetSummary.end || "")
      );
    },
    customHoursHint() {
      if (this.customOpen) return "Then tap save";
      const summary = this.sheetSummary;
      if (
        summary.kind === "work" &&
        summary.start &&
        summary.end &&
        !this.isShopHoursShift
      ) {
        return `${summary.start}–${summary.end}`;
      }
      return "Set a custom shift";
    },
    phoneStatusLine() {
      const cover = this.selectedCoverage;
      if (!cover.open) return "Closed";
      if (!cover.covered) return "No one working";
      const hours = shopHoursForDate(this.shop, this.selectedDate);
      const people = cover.working === 1 ? "1 on" : `${cover.working} on`;
      if (!hours) return people;
      return `${people} · ${hours.start}–${hours.end}`;
    },
    weekMotion() {
      return this.weekDir === "prev" ? "rv2-week-prev" : "rv2-week-next";
    },
    isClipboardWeek() {
      return !!(
        this.weekClipboard &&
        this.weekClipboard.weekStart === this.weekStart
      );
    },
    pasteDisabled() {
      return (
        this.busy ||
        !this.staff.length ||
        !this.weekClipboard ||
        this.isClipboardWeek
      );
    },
    copyButtonLabel() {
      if (this.clipFlash && this.isClipboardWeek) return "Copied";
      return "Copy week";
    },
    pasteButtonLabel() {
      if (!this.weekClipboard) return "Paste week";
      if (this.isClipboardWeek) return "Paste week";
      return "Paste here";
    },
    dockHint() {
      if (!this.weekClipboard) return "";
      if (this.isClipboardWeek) {
        return "Go to another week to paste.";
      }
      return "Paste fills empty days only.";
    },
    pendingGroups() {
      if (!this.pending || !this.pending.rows) return [];
      const map = {};
      this.pending.rows.forEach(row => {
        if (!map[row.resourceId]) {
          map[row.resourceId] = {
            resourceId: row.resourceId,
            name: row.name,
            rows: []
          };
        }
        map[row.resourceId].rows.push(row);
      });
      return Object.keys(map)
        .map(id => map[id])
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }
  },
  watch: {
    weekStart() {
      this.pending = null;
    },
    sheet() {
      this.lockPageScroll();
    },
    pending() {
      this.lockPageScroll();
    }
  },
  methods: {
    ...mapActions({
      fetchEmployees: "employees/fetchEmployees",
      fetchShop: "shops/fetchShop",
      subscribeRoster: "roster/subscribeRoster",
      unsubscribeRoster: "roster/unsubscribeRoster",
      applyRosterPlan: "roster/applyRosterPlan"
    }),
    defaultMode() {
      if (typeof window === "undefined") return "week";
      return window.innerWidth < 768 ? "day" : "week";
    },
    setMode(mode) {
      this.mode = mode;
      this.userChoseMode = true;
    },
    formatHoursLabel,
    staffInitials,
    tone(id) {
      return faceColor.hex(String(id || "staff"));
    },
    hoursPct(hours) {
      if (!hours) return 0;
      return Math.max(8, Math.min(100, (hours / 40) * 100));
    },
    markBroken(id) {
      this.$set(this.brokenImages, id, true);
    },
    summaryFor(personId, date) {
      return (
        (this.board[personId] && this.board[personId][date]) || {
          kind: "off",
          label: "Off",
          start: null,
          end: null,
          breakLabel: null,
          spanLabel: null,
          rangeLabel: null,
          classic: false
        }
      );
    },
    dayHeadClass(date) {
      const cover = this.coverage[date] || {};
      return {
        "is-today": date === this.today,
        "is-selected": date === this.selectedDate,
        "is-weekend": isWeekendIso(date),
        "is-closed": !cover.open,
        "is-gap": cover.open && !cover.covered
      };
    },
    cellClass(person, date) {
      const summary =
        (this.board[person._id] && this.board[person._id][date]) ||
        { kind: "off" };
      const cover = this.coverage[date] || {};
      return {
        ["is-" + summary.kind]: true,
        "is-today": date === this.today,
        "is-weekend": isWeekendIso(date),
        "is-closed": !cover.open,
        "is-selected":
          !!(this.sheet &&
            this.sheet.person._id === person._id &&
            this.sheet.date === date)
      };
    },
    cellAria(person, date) {
      const summary =
        (this.board[person._id] && this.board[person._id][date]) ||
        { label: "Off" };
      return `${person.name}, ${shortDayLabel(date)} ${monthDayLabel(date)}, ${summary.label}`;
    },
    shortDay: shortDayLabel,
    monthDay: monthDayLabel,
    shiftWeek(days) {
      this.weekDir = days > 0 ? "next" : "prev";
      const selectedIndex = this.week.indexOf(this.selectedDate);
      this.weekStart = addDaysIso(this.weekStart, days);
      if (selectedIndex !== -1) {
        this.selectedDate = this.week[selectedIndex];
      } else if (this.week.indexOf(this.selectedDate) === -1) {
        this.selectedDate = this.weekStart;
      }
    },
    goThisWeek() {
      const next = mondayOfWeek(this.today);
      if (next !== this.weekStart) {
        this.weekDir = next > this.weekStart ? "next" : "prev";
      }
      this.weekStart = next;
      this.selectedDate = this.today;
    },
    shiftDay(days) {
      this.weekDir = days > 0 ? "next" : "prev";
      this.selectedDate = addDaysIso(this.selectedDate, days);
      this.weekStart = mondayOfWeek(this.selectedDate);
    },
    openDay(date) {
      this.selectedDate = date;
      if (window.innerWidth < 768) {
        this.mode = "day";
        this.userChoseMode = true;
      }
    },
    openCell(person, date) {
      this.closeMore();
      const summary =
        (this.board[person._id] && this.board[person._id][date]) ||
        summarizeCell(eventsForCell(this.events, person._id, date), date);
      const hours = shopHoursForDate(this.shop, date);
      this.selectedDate = date;
      this.customOpen = false;
      this.sheet = {
        person,
        date,
        start: summary.start || (hours && hours.start) || "09:00",
        end: summary.end || (hours && hours.end) || "18:00"
      };
    },
    closeSheet() {
      this.sheet = null;
      this.customOpen = false;
    },
    toggleCustomHours() {
      this.customOpen = !this.customOpen;
    },
    closeMore() {
      this.moreOpen = false;
    },
    closePending() {
      this.pending = null;
    },
    lockPageScroll() {
      if (typeof document === "undefined") return;
      document.body.style.overflow = this.sheet || this.pending ? "hidden" : "";
    },
    jumpToday() {
      this.goThisWeek();
      this.closeMore();
    },
    runCopy() {
      this.closeMore();
      this.copyLastWeek();
    },
    runFill() {
      this.closeMore();
      this.fillDefaults();
    },
    runClear() {
      this.closeMore();
      this.clearWeek();
    },
    clipStorageKey() {
      const shop = this.shop;
      return `sb.rosterV2.clip.${(shop && shop._id) || "store"}`;
    },
    persistClipboard() {
      if (typeof window === "undefined" || !window.sessionStorage) return;
      try {
        if (!this.weekClipboard) {
          window.sessionStorage.removeItem(this.clipStorageKey());
          return;
        }
        window.sessionStorage.setItem(
          this.clipStorageKey(),
          JSON.stringify(this.weekClipboard)
        );
      } catch (error) {
        /* ignore quota / private mode */
      }
    },
    restoreClipboard() {
      if (typeof window === "undefined" || !window.sessionStorage) return;
      try {
        const raw = window.sessionStorage.getItem(this.clipStorageKey());
        if (!raw) return;
        const clip = JSON.parse(raw);
        if (clip && clip.weekStart && Array.isArray(clip.cells)) {
          this.weekClipboard = clip;
        }
      } catch (error) {
        this.weekClipboard = null;
      }
    },
    copyThisWeek() {
      this.closeSheet();
      const snapshot = snapshotWeek({
        events: this.events,
        resourceIds: this.staffIds,
        week: this.week
      });
      if (!snapshot.copied) {
        this.$notify({
          type: "success",
          text: "Nothing to copy. This week is empty."
        });
        return;
      }
      this.weekClipboard = {
        ...snapshot,
        weekDisplay: this.weekDisplay
      };
      this.persistClipboard();
      this.flashCopied();
      this.$notify({
        type: "success",
        text: `Copied ${this.weekDisplay}`
      });
    },
    flashCopied() {
      this.clipFlash = true;
      if (this.clipFlashTimer) clearTimeout(this.clipFlashTimer);
      this.clipFlashTimer = setTimeout(() => {
        this.clipFlash = false;
        this.clipFlashTimer = null;
      }, 1400);
    },
    pasteWeek() {
      this.closeSheet();
      if (!this.weekClipboard) {
        this.$notify({
          type: "error",
          text: "Copy a week first."
        });
        return;
      }
      if (this.isClipboardWeek) {
        this.$notify({
          type: "success",
          text: "Go to another week to paste."
        });
        return;
      }
      const plan = planPasteWeek({
        snapshot: this.weekClipboard,
        events: this.events,
        destWeek: this.week,
        resourceIds: this.staffIds
      });
      if (!plan.pasted) {
        this.$notify({
          type: "success",
          text: plan.sameWeek
            ? "This is the week you copied."
            : "Nothing to paste. Those days already have hours."
        });
        return;
      }
      const source = this.weekClipboard.weekDisplay;
      const count = plan.pasted;
      this.pending = {
        kind: "paste",
        plan,
        skipped: plan.skipped || 0,
        rows: previewRowsFromCreates(plan.creates, this.staff),
        kicker: `${source} to ${this.weekDisplay}`,
        title: "Paste this week",
        body: `Fill empty days from ${source}. Days that already have hours stay the same.`,
        action: `Paste ${count} day${count === 1 ? "" : "s"}`
      };
    },
    clearClipboard() {
      this.weekClipboard = null;
      this.persistClipboard();
    },
    async applyPlan(plan, successText) {
      if (this.busy) return;
      if (plan && plan.invalid) {
        this.$notify({
          type: "error",
          text: "Finish time must be after start time."
        });
        return;
      }
      this.busy = true;
      try {
        await this.applyRosterPlan(plan);
        if (successText) {
          this.$notify({ type: "success", text: successText });
        }
        this.closeSheet();
        this.pending = null;
      } catch (error) {
        this.$notify({
          type: "error",
          title: this.$t("message.error"),
          text: error.message || "Could not save"
        });
      } finally {
        this.busy = false;
      }
    },
    cellEvents() {
      return eventsForCell(this.events, this.sheet.person._id, this.sheet.date);
    },
    applyCellPlan(plan, successText, next) {
      if (plan && plan.invalid) {
        this.applyPlan(plan, successText);
        return;
      }
      const date = this.sheet.date;
      const person = this.sheet.person;
      const conflict = classicSpanConflict(this.cellEvents(), date);
      if (!conflict) {
        this.applyPlan(plan, successText);
        return;
      }
      const nextLabel = next && next.label;
      const nextKind = next && next.kind;
      this.pending = {
        kind: "span",
        danger: true,
        plan,
        successText,
        skipped: 0,
        rows: conflict.dates.map(day => {
          const current = this.summaryFor(person._id, day);
          const isThis = day === date;
          const after = isThis && nextLabel ? nextLabel : "Off";
          return {
            resourceId: person._id,
            name: person.name,
            date: day,
            dayLabel: formatDayShort(day),
            kind: isThis && nextKind ? nextKind : "off",
            label: `${current.label} → ${after}`
          };
        }),
        kicker: "Classic calendar",
        title: "This covers several days",
        body: formatClassicSpanWarning({
          rangeLabel: conflict.rangeLabel,
          date,
          nextLabel
        }),
        action: nextLabel ? "Replace the whole period" : "Remove the whole period"
      };
    },
    setShopHours() {
      const hours = this.sheetShopHours;
      if (!hours) {
        this.$notify({
          type: "error",
          text: "The shop is closed."
        });
        return;
      }
      this.applyCellPlan(
        planSetWork({
          cellEvents: this.cellEvents(),
          employee: this.sheet.person,
          date: this.sheet.date,
          startTime: hours.start,
          endTime: hours.end
        }),
        "Shift saved",
        { kind: "work", label: `${hours.start}–${hours.end}` }
      );
    },
    setCustomHours() {
      if (!isValidShiftRange(this.sheet.start, this.sheet.end)) {
        this.$notify({
          type: "error",
          text: "Finish time must be after start time."
        });
        return;
      }
      this.applyCellPlan(
        planSetWork({
          cellEvents: this.cellEvents(),
          employee: this.sheet.person,
          date: this.sheet.date,
          startTime: this.sheet.start,
          endTime: this.sheet.end
        }),
        "Shift saved",
        { kind: "work", label: `${this.sheet.start}–${this.sheet.end}` }
      );
    },
    setOff() {
      this.applyCellPlan(planClearDay(this.cellEvents()), "Marked off", null);
    },
    setLeave(type) {
      const sick = type === "RESOURCE_SICK";
      this.applyCellPlan(
        planSetLeave({
          cellEvents: this.cellEvents(),
          employee: this.sheet.person,
          date: this.sheet.date,
          type: sick ? RESOURCE_SICK : RESOURCE_HOLIDAY
        }),
        sick ? "Marked sick" : "Marked away",
        { kind: sick ? "sick" : "holiday", label: sick ? "Sick" : "Away" }
      );
    },
    copyLastWeek() {
      this.closeSheet();
      const previousStart = addDaysIso(this.weekStart, -7);
      const previousLabel = formatWeekDisplay(previousStart, this.today);
      const plan = planCopyPreviousWeek({
        events: this.events,
        resourceIds: this.staffIds,
        currentWeek: this.week,
        previousWeek: weekDates(previousStart)
      });
      if (!plan.copied) {
        this.$notify({
          type: "success",
          text: `Nothing to copy from ${previousLabel}.`
        });
        return;
      }
      const count = plan.copied;
      this.pending = {
        kind: "copy",
        plan,
        skipped: plan.skipped || 0,
        rows: previewRowsFromCreates(plan.creates, this.staff),
        kicker: `${previousLabel} to ${this.weekDisplay}`,
        title: "Use the week before",
        body: `Fill empty days from ${previousLabel}.`,
        action: `Fill ${count} day${count === 1 ? "" : "s"}`
      };
    },
    clearWeek() {
      this.closeSheet();
      const plan = planClearWeek({
        events: this.events,
        resourceIds: this.staffIds,
        week: this.week
      });
      if (!plan.cleared) {
        this.$notify({
          type: "success",
          text: "This week is already empty."
        });
        return;
      }
      const count = plan.cleared;
      const beyond = spanningOutsideWeek({
        events: this.events,
        resourceIds: this.staffIds,
        week: this.week
      });
      this.pending = {
        kind: "clear",
        danger: true,
        plan,
        skipped: 0,
        rows: previewRowsFromCreates(plan.deletes, this.staff),
        kicker: this.weekDisplay,
        title: "Clear this week?",
        body:
          "Everyone will be marked Off. Work, lunch, sick and away days are removed so you can paste another week. Bookings stay.",
        note: beyond.length
          ? "Some classic-calendar events also cover days outside this week. Those days will be removed too."
          : "",
        action: `Clear ${count} day${count === 1 ? "" : "s"}`
      };
    },
    fillDefaults() {
      this.closeSheet();
      const plan = planFillDefaults({
        events: this.events,
        employees: this.staff,
        shop: this.shop,
        week: this.week
      });
      if (!plan.filled) {
        this.$notify({
          type: "success",
          text: "Nothing to fill. No empty days match usual hours."
        });
        return;
      }
      const count = plan.filled;
      this.pending = {
        kind: "fill",
        plan,
        skipped: plan.skipped || 0,
        rows: previewRowsFromCreates(plan.creates, this.staff),
        kicker: this.weekDisplay,
        title: "Use usual hours",
        body: "Uses the usual hours saved on each person. Empty days only.",
        action: `Add ${count} day${count === 1 ? "" : "s"}`
      };
    },
    confirmPending() {
      if (!this.pending || this.busy) return;
      if (this.pending.successText) {
        this.applyPlan(this.pending.plan, this.pending.successText);
        return;
      }
      const verb =
        this.pending.kind === "paste"
          ? "Pasted"
          : this.pending.kind === "copy"
            ? "Filled"
            : this.pending.kind === "clear"
              ? "Cleared"
              : "Added";
      const count =
        this.pending.plan.pasted ||
        this.pending.plan.copied ||
        this.pending.plan.filled ||
        this.pending.plan.cleared ||
        0;
      this.applyPlan(
        this.pending.plan,
        `${verb} ${count} day${count === 1 ? "" : "s"}`
      );
    },
    onKeydown(event) {
      if (event.key !== "Escape") return;
      if (this.pending) {
        this.closePending();
        return;
      }
      if (this.customOpen) {
        this.customOpen = false;
        return;
      }
      this.closeSheet();
    },
    onResize() {
      this.mode = this.defaultMode();
    },
    paintChrome(on) {
      if (typeof document === "undefined") return;
      const panel = document.querySelector(".main-panel");
      if (panel) {
        panel.classList.toggle("rv2-surface", on);
      }
      document.body.classList.toggle("rv2-surface", on);
    },
    teardownChrome() {
      this.paintChrome(false);
      document.body.style.overflow = "";
      window.removeEventListener("keydown", this.onKeydown);
      window.removeEventListener("resize", this.onResize);
      if (this.clipFlashTimer) {
        clearTimeout(this.clipFlashTimer);
        this.clipFlashTimer = null;
      }
    }
  },
  async created() {
    this.paintChrome(true);
    try {
      await Promise.all([this.fetchEmployees(), this.fetchShop()]);
      this.restoreClipboard();
      this.subscribeRoster();
    } catch (error) {
      this.$notify({
        type: "error",
        title: this.$t("message.error"),
        text: error.message || "Could not load"
      });
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);
    this.$nextTick(() => {
      this.ready = true;
    });
  },
  beforeDestroy() {
    this.unsubscribeRoster();
    this.teardownChrome();
  },
  beforeRouteLeave(to, from, next) {
    this.teardownChrome();
    next();
  }
};
</script>

<style scoped>
.rv2 {
  --ink: #14110e;
  --ink-soft: #3c352e;
  --muted: #7a7268;
  --faint: #b3aaa0;
  --line: rgba(20, 17, 14, 0.08);
  --line-strong: rgba(20, 17, 14, 0.14);
  --canvas: #efe8dc;
  --surface: #fbf7f0;
  --surface-2: #fffdf8;
  --work: #d7e4d2;
  --work-ink: #1c4631;
  --sick: #f3d6c3;
  --sick-ink: #8a3c14;
  --holiday: #ddd4ef;
  --holiday-ink: #3f3274;
  --gap: #efcfc6;
  --today: #f6ead0;
  --shadow: 0 1px 0 rgba(255, 255, 255, 0.65) inset, 0 18px 40px rgba(42, 28, 14, 0.07),
    0 2px 6px rgba(42, 28, 14, 0.04);
  --shadow-hover: 0 1px 0 rgba(255, 255, 255, 0.8) inset, 0 18px 36px rgba(42, 28, 14, 0.14);
  --ease: cubic-bezier(0.22, 1, 0.36, 1);
  --font: "Instrument Sans", "Montserrat", "Helvetica Neue", sans-serif;
  --display: "Instrument Serif", "Iowan Old Style", Georgia, serif;
  --radius: 28px;
  position: relative;
  max-width: none;
  margin: -86px -15px 0;
  min-height: calc(100vh - 62px);
  padding: 18px 16px 56px;
  color: var(--ink);
  font-family: var(--font);
  background:
    radial-gradient(900px 320px at 8% -10%, rgba(255, 255, 255, 0.7), transparent 58%),
    radial-gradient(720px 260px at 100% 0%, rgba(232, 214, 186, 0.4), transparent 52%),
    var(--canvas);
  -webkit-font-smoothing: antialiased;
}

.rv2-progress {
  position: sticky;
  top: 0;
  z-index: 8;
  height: 2px;
  margin: -18px -16px 16px;
  overflow: hidden;
  background: rgba(20, 17, 14, 0.08);
}

.rv2-progress i {
  display: block;
  width: 36%;
  height: 100%;
  background: var(--ink);
  animation: rv2-load 1.05s ease infinite;
}

.rv2-top {
  display: grid;
  gap: 18px;
  margin-bottom: 18px;
}

.rv2-hero {
  display: grid;
  grid-template-columns: 48px 1fr 48px;
  align-items: center;
  gap: 8px;
}

.rv2-hero-copy {
  overflow: hidden;
  text-align: center;
}

.rv2-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.rv2-kicker i {
  padding: 2px 7px;
  border-radius: 999px;
  background: var(--ink);
  color: #f7f1e7;
  font-style: normal;
  letter-spacing: 0.12em;
}

.rv2-title {
  margin: 6px 0 4px;
  font-family: var(--display);
  font-size: clamp(34px, 6vw, 56px);
  font-weight: 400;
  letter-spacing: -0.03em;
  line-height: 0.95;
}

.rv2-sub {
  margin: 0;
  color: var(--ink-soft);
  font-size: 14px;
  font-weight: 500;
}

.rv2-toolbar,
.rv2-toolbar-end,
.rv2-day-hero,
.rv2-card,
.rv2-card-who,
.rv2-sheet-head,
.rv2-legend {
  display: flex;
  align-items: center;
}

.rv2-toolbar,
.rv2-toolbar-end {
  flex-wrap: wrap;
  gap: 8px;
}

.rv2-toolbar-end {
  margin-left: auto;
}

.rv2-clip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 18px;
  background: var(--surface);
  box-shadow: inset 0 0 0 1px var(--line);
}

.rv2-clip p {
  margin: 0;
  color: var(--ink-soft);
  font-size: 14px;
  font-weight: 500;
}

.rv2-clip-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rv2-dock {
  display: none;
}

.rv2-chev,
.rv2-chip-btn,
.rv2-mode-btn,
.rv2-text-btn,
.rv2-primary,
.rv2-close,
.rv2-choice,
.rv2-cell,
.rv2-dayhead,
.rv2-card,
.rv2-strip-day,
.rv2-strip-nav,
.rv2-dock-btn,
.rv2-dock-clear {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;
}

.rv2-chev {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  color: var(--ink);
  background: rgba(255, 253, 248, 0.55);
  box-shadow: inset 0 0 0 1px var(--line);
  transition: transform 0.22s var(--ease), box-shadow 0.22s ease, background 0.22s ease;
}

.rv2-chev svg {
  width: 22px;
  height: 22px;
  display: block;
  margin: 0 auto;
}

.rv2-chev:hover,
.rv2-mini-chev:hover,
.rv2-more-btn:hover,
.rv2-chip-btn:hover {
  background: var(--surface-2);
  box-shadow: var(--shadow-hover);
  transform: translateY(-1px);
}

.rv2-chev:active,
.rv2-mini-chev:active,
.rv2-more-btn:active,
.rv2-chip-btn:active,
.rv2-primary:active {
  transform: scale(0.96);
}

.rv2-chip-btn,
.rv2-mode-btn,
.rv2-text-btn,
.rv2-close {
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 253, 248, 0.7);
  box-shadow: inset 0 0 0 1px var(--line);
  font-size: 13px;
  font-weight: 600;
  transition: transform 0.22s var(--ease), box-shadow 0.22s ease, background 0.22s ease,
    opacity 0.22s ease;
}

.rv2-chip-btn.is-on,
.rv2-chip-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

.rv2-chip-btn.is-danger {
  background: #8f2d1f;
  color: #f8efe8;
  box-shadow: none;
}

.rv2-chip-btn.is-danger:hover:not(:disabled) {
  background: #7a2419;
  color: #f8efe8;
}

.rv2-chip-btn.is-danger:disabled {
  opacity: 0.38;
}

.rv2-primary {
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  background: var(--ink);
  color: #f6efe4;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 10px 22px rgba(20, 17, 14, 0.16);
  transition: transform 0.22s var(--ease), box-shadow 0.22s ease, background 0.22s ease;
}

.rv2-primary:hover {
  box-shadow: 0 16px 30px rgba(20, 17, 14, 0.22);
  transform: translateY(-1px);
}

.rv2-primary.is-quiet {
  background: transparent;
  color: var(--ink);
  box-shadow: inset 0 0 0 1px var(--line-strong);
}

.rv2-primary.is-danger {
  background: #8f2d1f;
  color: #f8efe8;
  box-shadow: 0 10px 22px rgba(143, 45, 31, 0.22);
}

.rv2-primary.is-danger:hover {
  background: #7a2419;
  box-shadow: 0 16px 30px rgba(143, 45, 31, 0.28);
}

.rv2-preview-lead.is-warn {
  color: #8a3c14;
}

.rv2-classic {
  padding: 10px 4px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid var(--line-strong);
  transition: color 0.2s ease, border-color 0.2s ease;
}

.rv2-classic:hover {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.rv2-mode {
  display: inline-flex;
  padding: 3px;
  border-radius: 999px;
  background: rgba(20, 17, 14, 0.06);
}

.rv2-mode-btn.is-on {
  background: var(--surface-2);
  box-shadow: 0 1px 2px rgba(20, 17, 14, 0.08);
}

.rv2-preview {
  max-height: min(88vh, 720px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.rv2-preview h2 {
  max-width: 20ch;
}

.rv2-preview-lead,
.rv2-preview-note {
  margin: 8px 0 0;
  color: var(--ink-soft);
  font-size: 14px;
  line-height: 1.45;
}

.rv2-preview-note {
  color: var(--muted);
}

.rv2-preview-list {
  display: grid;
  gap: 14px;
  margin: 18px 0;
}

.rv2-preview-group strong {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 700;
}

.rv2-preview-group ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.rv2-preview-group li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid var(--line);
  font-size: 14px;
}

.rv2-preview-group li span {
  color: var(--muted);
  font-weight: 600;
}

.rv2-preview-group li b {
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.rv2-preview-group li b.is-work {
  background: var(--work);
  color: var(--work-ink);
}

.rv2-preview-group li b.is-sick {
  background: var(--sick);
  color: var(--sick-ink);
}

.rv2-preview-group li b.is-holiday {
  background: var(--holiday);
  color: var(--holiday-ink);
}

.rv2-preview-group li b.is-off {
  background: #efe8dc;
  color: var(--ink-soft);
}

.rv2-preview-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.rv2-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.rv2-metric {
  flex: 1 1 104px;
  max-width: 220px;
  padding: 12px 14px 10px;
  border-radius: 18px;
  background: var(--surface);
  box-shadow: inset 0 0 0 1px var(--line);
  transition: transform 0.28s var(--ease), box-shadow 0.28s ease;
}

.rv2-metric:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}

.rv2-metric strong {
  display: block;
  overflow: hidden;
  font-family: var(--display);
  font-size: 28px;
  font-weight: 400;
  letter-spacing: -0.03em;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rv2-metric span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.rv2-metric.is-warn {
  background: #f8e4dc;
}

.rv2-metric.is-wait {
  min-height: 72px;
  background: linear-gradient(90deg, #e7dfd1, #f4efe6, #e7dfd1);
  background-size: 200% 100%;
  animation: rv2-shimmer 1.3s ease infinite;
}

.rv2-board {
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: var(--radius);
  background: var(--surface);
  box-shadow: var(--shadow), inset 0 0 0 1px var(--line);
}

.rv2-grid {
  display: grid;
  min-width: 860px;
}

.rv2-corner,
.rv2-dayhead,
.rv2-name,
.rv2-cell {
  min-height: 84px;
  padding: 10px 8px;
  border-bottom: 1px solid var(--line);
  text-align: left;
}

.rv2-corner,
.rv2-name {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #f7f2ea;
}

.rv2-dayhead {
  position: sticky;
  top: 0;
  z-index: 3;
  background: rgba(251, 247, 240, 0.96);
  backdrop-filter: blur(10px);
}

.rv2-corner {
  top: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.rv2-dow {
  display: block;
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.rv2-dom {
  display: block;
  margin: 2px 0 6px;
  font-family: var(--display);
  font-size: 24px;
  line-height: 1;
}

.rv2-pill {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(20, 17, 14, 0.06);
  color: var(--muted);
}

.rv2-pill.is-gap {
  background: var(--gap);
  color: #7a2d1a;
}

.rv2-pill.is-ok {
  background: var(--work);
  color: var(--work-ink);
}

.rv2-name {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rv2-name-copy {
  min-width: 0;
}

.rv2-name-copy strong,
.rv2-card-who strong {
  display: block;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rv2-name-copy span,
.rv2-card-who span,
.rv2-choice span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.rv2-face {
  position: relative;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  overflow: hidden;
  border-radius: 50%;
  color: #f7f1e8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 34px;
  text-align: center;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 4px 10px rgba(20, 17, 14, 0.14);
}

.rv2-face img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rv2-face.is-lg {
  width: 52px;
  height: 52px;
  font-size: 16px;
  line-height: 52px;
}

.rv2-hours-track {
  display: block;
  width: 72px;
  height: 3px;
  margin-top: 6px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(20, 17, 14, 0.08);
}

.rv2-hours-track i {
  display: block;
  height: 100%;
  background: var(--work-ink);
  transition: width 0.7s var(--ease);
}

.rv2-cell {
  background: transparent;
}

.rv2-chip {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 62px;
  padding: 8px 9px;
  border-radius: 16px;
  background: rgba(20, 17, 14, 0.025);
  box-shadow: inset 0 0 0 1px var(--line);
  transition: transform 0.22s var(--ease), box-shadow 0.22s ease, background 0.22s ease,
    color 0.22s ease;
}

.rv2-cell:hover .rv2-chip {
  transform: translateY(-2px) scale(1.015);
  box-shadow: 0 12px 24px rgba(42, 28, 14, 0.12);
}

.rv2-cell:active .rv2-chip {
  transform: scale(0.98);
}

.rv2-cell-label,
.rv2-card-status b {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}

.rv2-cell-break,
.rv2-card-status small {
  display: block;
  margin-top: 2px;
  color: inherit;
  opacity: 0.72;
  font-size: 10px;
  font-weight: 600;
}

.rv2-cell.is-work .rv2-chip,
.rv2-card.is-work,
.rv2-choice.is-work {
  background: var(--work);
  color: var(--work-ink);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.rv2-cell.is-work:hover .rv2-chip {
  box-shadow: 0 14px 26px rgba(28, 70, 49, 0.16);
}

.rv2-cell.is-sick .rv2-chip,
.rv2-card.is-sick,
.rv2-choice.is-sick {
  background: var(--sick);
  color: var(--sick-ink);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.rv2-cell.is-sick:hover .rv2-chip {
  box-shadow: 0 14px 26px rgba(138, 60, 20, 0.14);
}

.rv2-cell.is-holiday .rv2-chip,
.rv2-card.is-holiday,
.rv2-choice.is-holiday {
  background: var(--holiday);
  color: var(--holiday-ink);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.rv2-cell.is-holiday:hover .rv2-chip {
  box-shadow: 0 14px 26px rgba(63, 50, 116, 0.14);
}

.rv2-cell.is-off .rv2-chip {
  background: transparent;
  box-shadow: inset 0 0 0 1px dashed rgba(20, 17, 14, 0.16);
  color: var(--muted);
}

.rv2-cell.is-closed .rv2-chip {
  opacity: 0.55;
}

.rv2-cell.is-today,
.rv2-dayhead.is-today {
  background: linear-gradient(180deg, var(--today), transparent 88%);
}

.rv2-dayhead.is-gap {
  background: #f8e7e1;
}

.rv2-dayhead.is-closed {
  opacity: 0.62;
}

.rv2-cell.is-selected .rv2-chip,
.rv2-card.is-selected {
  box-shadow: inset 0 0 0 1.5px var(--ink), 0 10px 22px rgba(20, 17, 14, 0.1);
}

.rv2-cell:focus-visible,
.rv2-dayhead:focus-visible,
.rv2-chip-btn:focus-visible,
.rv2-choice:focus-visible,
.rv2-card:focus-visible,
.rv2-dock-btn:focus-visible,
.rv2-dock-clear:focus-visible,
.rv2-strip-nav:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}

.rv2-strip {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}

.rv2-strip-nav {
  display: none;
}

.rv2-strip-day {
  display: grid;
  gap: 2px;
  min-width: 0;
  min-height: 64px;
  padding: 8px 2px;
  border-radius: 18px;
  background: var(--surface);
  box-shadow: inset 0 0 0 1px var(--line);
  text-align: center;
  transition: transform 0.22s var(--ease), background 0.25s ease, color 0.25s ease,
    box-shadow 0.25s ease;
}

.rv2-strip-day:active {
  transform: scale(0.96);
}

.rv2-strip-day span {
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.rv2-strip-day b {
  font-family: var(--display);
  font-size: 20px;
  font-weight: 400;
}

.rv2-strip-day.is-today:not(.is-selected) {
  background: #f4e4bc;
  color: var(--ink);
  box-shadow: inset 0 0 0 1.5px rgba(140, 100, 30, 0.28);
}

.rv2-strip-day.is-today:not(.is-selected) span {
  color: #8a6d3b;
}

.rv2-strip-day.is-selected {
  background: var(--ink);
  color: #f6efe4;
  box-shadow: 0 10px 20px rgba(20, 17, 14, 0.2);
}

.rv2-strip-day.is-selected span {
  color: rgba(246, 239, 228, 0.7);
}

.rv2-strip-day.is-gap:not(.is-selected):not(.is-today) {
  background: #f8e4dc;
}

.rv2-dayboard {
  display: grid;
  gap: 10px;
}

.rv2-day-hero {
  justify-content: space-between;
  margin-bottom: 4px;
  text-align: center;
}

.rv2-day-hero strong {
  display: block;
  font-family: var(--display);
  font-size: 24px;
  font-weight: 400;
}

.rv2-day-hero span {
  color: var(--muted);
  font-size: 13px;
}

.rv2-card {
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 22px;
  background: var(--surface);
  box-shadow: 0 10px 30px rgba(42, 28, 14, 0.04), inset 0 0 0 1px var(--line);
  transition: transform 0.22s var(--ease), box-shadow 0.22s ease;
}

.rv2-card:active {
  transform: scale(0.985);
}

.rv2-card-who {
  flex: 1 1 auto;
  gap: 12px;
  min-width: 0;
  text-align: left;
}

.rv2-card-status {
  flex: 0 0 auto;
  text-align: right;
}

.rv2-card-status b {
  display: block;
  white-space: nowrap;
}

.rv2-legend {
  gap: 16px;
  justify-content: center;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.rv2-legend i {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 50%;
  background: #ddd6cc;
  box-shadow: inset 0 0 0 1px var(--line);
}

.rv2-legend .is-work { background: var(--work); }
.rv2-legend .is-sick { background: var(--sick); }
.rv2-legend .is-holiday { background: var(--holiday); }

.rv2-phone,
.rv2-phone-status {
  display: none;
}

.rv2-phone-week {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rv2-phone-week-copy {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-align: left;
}

.rv2-phone-week-copy span {
  display: block;
  overflow: hidden;
  margin-bottom: 4px;
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.rv2-phone-week-copy strong {
  display: block;
  overflow: hidden;
  font-family: var(--display);
  font-size: 22px;
  font-weight: 400;
  letter-spacing: -0.03em;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rv2-mini-chev,
.rv2-more-btn {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 253, 248, 0.7);
  box-shadow: inset 0 0 0 1px var(--line);
  color: var(--ink);
}

.rv2-mini-chev svg,
.rv2-more-btn svg {
  display: block;
  width: 20px;
  height: 20px;
  margin: 0 auto;
}

.rv2-more {
  position: relative;
}

.rv2-mini-chev,
.rv2-more-btn {
  transition: transform 0.22s var(--ease), box-shadow 0.22s ease, background 0.22s ease;
}

.rv2-more-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 12;
  display: grid;
  min-width: 220px;
  padding: 6px;
  border-radius: 16px;
  background: var(--surface-2);
  box-shadow: 0 22px 50px rgba(20, 17, 14, 0.18);
  transform-origin: top right;
}

.rv2-more-menu button,
.rv2-more-menu a {
  display: block;
  width: 100%;
  padding: 12px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.rv2-more-menu button:disabled {
  opacity: 0.4;
}

.rv2-more-menu button.is-danger {
  color: #8f2d1f;
}

.rv2-more-menu button:not(:disabled):active,
.rv2-more-menu a:active {
  background: rgba(20, 17, 14, 0.06);
}

.rv2-phone-status {
  margin: 0 2px 12px;
  color: var(--ink-soft);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

.rv2-strip-dot {
  display: block;
  width: 5px;
  height: 5px;
  margin: 3px auto 0;
  border-radius: 50%;
  background: #b4532a;
}

.rv2-strip-day.is-selected .rv2-strip-dot {
  background: #f3c2a8;
}

.rv2-card-shift {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(20, 17, 14, 0.04);
  color: var(--ink-soft);
  text-align: left;
}

.rv2-card-shift b {
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}

.rv2-card-shift small {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  opacity: 0.75;
}

.rv2-card-shift.is-work {
  background: var(--work);
  color: var(--work-ink);
}

.rv2-card-shift.is-sick {
  background: var(--sick);
  color: var(--sick-ink);
}

.rv2-card-shift.is-holiday {
  background: var(--holiday);
  color: var(--holiday-ink);
}

.rv2-card-shift.is-off {
  background: transparent;
  box-shadow: inset 0 0 0 1px dashed rgba(20, 17, 14, 0.16);
}

.rv2-empty,
.rv2-loading {
  max-width: 460px;
  margin: 40px auto;
  text-align: center;
}

.rv2-empty-kicker {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.rv2-empty h2 {
  margin: 8px 0 10px;
  font-family: var(--display);
  font-size: 36px;
  font-weight: 400;
}

.rv2-empty p {
  color: var(--ink-soft);
}

.rv2-skel-board {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.rv2-skel-cell {
  height: 72px;
  border-radius: 16px;
  background: linear-gradient(90deg, #e7dfd1, #f4efe6, #e7dfd1);
  background-size: 200% 100%;
  animation: rv2-shimmer 1.3s ease infinite;
}

.rv2-scrim {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(20, 16, 12, 0.38);
  backdrop-filter: blur(10px);
}

.rv2-scrim.is-front {
  z-index: 50;
}

.rv2-sheet {
  width: min(520px, 100%);
  padding: 8px 18px calc(18px + env(safe-area-inset-bottom));
  border-radius: 28px 28px 0 0;
  background: var(--surface-2);
  box-shadow: 0 -24px 80px rgba(20, 17, 14, 0.22);
}

.rv2-sheet-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 28px;
  margin: 0 0 8px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.rv2-sheet-handle i {
  display: block;
  width: 42px;
  height: 4px;
  border-radius: 99px;
  background: #d8d0c4;
  transition: background 0.2s ease, transform 0.2s var(--ease);
}

.rv2-sheet-handle:hover i,
.rv2-sheet-handle:active i {
  background: #b6aea2;
  transform: scaleX(1.12);
}

.rv2-sheet-head {
  gap: 14px;
}

.rv2-sheet-kicker {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.rv2-sheet h2 {
  margin: 0 0 6px;
  font-family: var(--display);
  font-size: 30px;
  font-weight: 400;
  letter-spacing: -0.03em;
}

.rv2-sheet-now {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}

.rv2-sheet-span {
  margin: 4px 0 0;
  color: #8a3c14;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.rv2-choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 18px 0;
}

.rv2-choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 78px;
  padding: 12px 14px;
  border-radius: 18px;
  background: #f3eee6;
  text-align: left;
  transition: transform 0.22s var(--ease), box-shadow 0.22s ease, background 0.22s ease;
}

.rv2-choice:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(42, 28, 14, 0.1);
}

.rv2-choice:disabled {
  opacity: 0.45;
  cursor: default;
}

.rv2-custom {
  grid-column: 1 / -1;
  display: grid;
  gap: 0;
  border-radius: 18px;
  background: transparent;
}

.rv2-custom.is-open {
  background: #f3eee6;
}

.rv2-choice.is-custom {
  position: relative;
  width: 100%;
  padding-right: 40px;
}

.rv2-choice.is-custom::after {
  content: "";
  position: absolute;
  top: 50%;
  right: 18px;
  width: 8px;
  height: 8px;
  border-right: 1.7px solid currentColor;
  border-bottom: 1.7px solid currentColor;
  opacity: 0.5;
  transform: translateY(-70%) rotate(45deg);
  transition: transform 0.2s var(--ease);
}

.rv2-choice.is-custom.is-open::after {
  transform: translateY(-20%) rotate(225deg);
}

.rv2-custom.is-open .rv2-choice.is-custom,
.rv2-custom.is-open .rv2-choice.is-custom:hover {
  transform: none;
  box-shadow: none;
  background: transparent;
}

.rv2-custom-form {
  display: grid;
  gap: 10px;
  padding: 0 14px 14px;
}

.rv2-times {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.rv2-custom-form label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.rv2-custom-form input {
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--line-strong);
  border-radius: 14px;
  background: #fff;
  color: var(--ink);
  font: inherit;
}

.rv2-save-hours,
.rv2-close {
  width: 100%;
}

.rv2-close {
  background: transparent;
  box-shadow: none;
  color: var(--muted);
}

.is-busy .rv2-board,
.is-busy .rv2-dayboard {
  opacity: 0.62;
  pointer-events: none;
  transition: opacity 0.28s ease;
}

.rv2-top,
.rv2-phone,
.rv2-dock,
.rv2-metrics,
.rv2-board,
.rv2-dayboard {
  opacity: 0;
  transform: translateY(10px);
}

.rv2.is-ready .rv2-top,
.rv2.is-ready .rv2-phone,
.rv2.is-ready .rv2-dock {
  animation: rv2-in 0.55s var(--ease) forwards;
}

.rv2.is-ready .rv2-metrics {
  animation: rv2-in 0.55s 0.05s var(--ease) forwards;
}

.rv2.is-ready .rv2-board,
.rv2.is-ready .rv2-dayboard {
  animation: rv2-in 0.6s 0.09s var(--ease) forwards;
}

.rv2-overlay-enter-active,
.rv2-overlay-leave-active {
  transition: opacity 0.28s ease;
}

.rv2-overlay-enter-active .rv2-sheet,
.rv2-overlay-leave-active .rv2-sheet {
  transition: transform 0.38s var(--ease), opacity 0.28s ease;
}

.rv2-overlay-enter,
.rv2-overlay-leave-to {
  opacity: 0;
}

.rv2-overlay-enter .rv2-sheet,
.rv2-overlay-leave-to .rv2-sheet {
  transform: translateY(22px);
  opacity: 0;
}

.rv2-pop-enter-active,
.rv2-pop-leave-active {
  transition: opacity 0.18s ease, transform 0.22s var(--ease);
}

.rv2-pop-enter,
.rv2-pop-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

.rv2-fade-enter-active,
.rv2-fade-leave-active {
  transition: opacity 0.22s ease;
}

.rv2-fade-enter,
.rv2-fade-leave-to {
  opacity: 0;
}

.rv2-week-next-enter-active,
.rv2-week-next-leave-active,
.rv2-week-prev-enter-active,
.rv2-week-prev-leave-active {
  transition: opacity 0.28s var(--ease), transform 0.32s var(--ease);
}

.rv2-week-next-leave-to,
.rv2-week-prev-enter {
  opacity: 0;
  transform: translateX(-18px);
}

.rv2-week-next-enter,
.rv2-week-prev-leave-to {
  opacity: 0;
  transform: translateX(18px);
}

@keyframes rv2-in {
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes rv2-load {
  0% { transform: translateX(-120%); }
  100% { transform: translateX(320%); }
}

@keyframes rv2-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .rv2-top,
  .rv2-phone,
  .rv2-dock,
  .rv2-metrics,
  .rv2-board,
  .rv2-dayboard,
  .rv2.is-ready .rv2-top,
  .rv2.is-ready .rv2-phone,
  .rv2.is-ready .rv2-dock,
  .rv2.is-ready .rv2-metrics,
  .rv2.is-ready .rv2-board,
  .rv2.is-ready .rv2-dayboard {
    opacity: 1;
    transform: none;
    animation: none;
  }

  .rv2 *,
  .rv2 *::before,
  .rv2 *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  .rv2-dock-reveal {
    transition: none;
  }
}

@media (min-width: 768px) {
  .rv2 {
    margin: -88px -30px 0;
    padding: 22px 28px 72px;
  }

  .rv2-progress {
    margin: -22px -28px 18px;
  }

  .rv2-mode {
    display: none;
  }

  .rv2-scrim {
    align-items: center;
    padding: 24px;
  }

  .rv2-sheet {
    border-radius: 28px;
  }

  .rv2-overlay-enter .rv2-sheet,
  .rv2-overlay-leave-to .rv2-sheet {
    transform: translateY(12px) scale(0.98);
  }
}

@media (max-width: 767px) {
  .rv2 {
    margin: 0 -15px 0;
    min-height: 0;
    padding: 12px 16px calc(96px + env(safe-area-inset-bottom, 0px));
  }

  .rv2.has-clip {
    padding-bottom: calc(168px + env(safe-area-inset-bottom, 0px));
  }

  .rv2-progress {
    margin: -12px -16px 14px;
  }

  .rv2-top-desktop,
  .rv2-metrics,
  .rv2-day-hero,
  .rv2-legend,
  .rv2-mode,
  .rv2-board {
    display: none !important;
  }

  .rv2-phone,
  .rv2-phone-status {
    display: block;
  }

  .rv2-phone {
    margin: 0 0 12px;
  }

  .rv2-top {
    margin-bottom: 10px;
  }

  .rv2-clip {
    display: none !important;
  }

  .rv2-strip {
    grid-template-columns: 36px repeat(7, minmax(0, 1fr)) 36px;
    gap: 4px;
    margin: 0 0 14px;
    align-items: stretch;
  }

  .rv2-strip-nav {
    display: grid;
    place-items: center;
    min-width: 0;
    min-height: 66px;
    padding: 0;
    border-radius: 16px;
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--line);
    color: var(--ink);
  }

  .rv2-strip-nav svg {
    width: 18px;
    height: 18px;
  }

  .rv2-strip-nav:active {
    transform: scale(0.96);
  }

  .rv2-dock {
    display: flex;
    flex-direction: column-reverse;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    margin: 0;
    padding: 10px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    border-radius: 22px 22px 0 0;
    background: rgba(251, 247, 240, 0.94);
    box-shadow: 0 -10px 28px rgba(42, 28, 14, 0.08);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    transition: box-shadow 0.28s ease, background 0.28s ease;
  }

  .rv2.has-sheet .rv2-dock {
    visibility: hidden;
    pointer-events: none;
  }

  .rv2-dock.is-armed {
    background: #fffdf8;
    box-shadow: 0 14px 32px rgba(42, 28, 14, 0.08), inset 0 0 0 1px rgba(20, 17, 14, 0.1);
  }

  .rv2-dock.is-flash {
    box-shadow: 0 14px 32px rgba(42, 28, 14, 0.1), inset 0 0 0 1.5px rgba(140, 100, 30, 0.35);
  }

  .rv2-dock-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .rv2-dock-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 12px;
    border-radius: 16px;
    background: rgba(255, 253, 248, 0.9);
    box-shadow: inset 0 0 0 1px var(--line);
    color: var(--ink);
    font-size: 14px;
    font-weight: 700;
    transition: transform 0.22s var(--ease), box-shadow 0.22s ease, background 0.22s ease,
      color 0.22s ease, opacity 0.22s ease;
  }

  .rv2-dock-btn svg {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
  }

  .rv2-dock-btn:active {
    transform: scale(0.97);
  }

  .rv2-dock-btn:disabled {
    opacity: 0.42;
    cursor: default;
    transform: none;
  }

  .rv2-dock.is-armed:not(.is-here) .rv2-dock-btn.is-paste:not(:disabled) {
    background: var(--ink);
    color: #f6efe4;
    box-shadow: 0 10px 20px rgba(20, 17, 14, 0.18);
  }

  .rv2-dock-btn.is-copy {
    background: #f3eee6;
  }

  .rv2-dock.is-flash .rv2-dock-btn.is-copy {
    background: #f4e4bc;
  }

  .rv2-dock-reveal {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.36s var(--ease);
  }

  .rv2-dock-reveal.is-open {
    grid-template-rows: 1fr;
  }

  .rv2-dock-reveal-inner {
    overflow: hidden;
  }

  .rv2-dock-status,
  .rv2-dock-hint {
    margin: 0;
  }

  .rv2-dock-reveal.is-open .rv2-dock-reveal-inner {
    padding-bottom: 8px;
  }

  .rv2-dock-status {
    padding: 2px 8px 0;
    color: var(--ink);
    font-size: 14px;
    font-weight: 500;
  }

  .rv2-dock-status strong {
    font-weight: 700;
  }

  .rv2-dock-hint {
    padding: 6px 8px 0;
    color: var(--ink-soft);
    font-size: 13px;
    font-weight: 500;
    line-height: 1.45;
  }

  .rv2-dock-clear {
    display: block;
    width: 100%;
    min-height: 40px;
    margin-top: 4px;
    padding: 8px;
    color: var(--muted);
    font-size: 13px;
    font-weight: 600;
    text-align: center;
  }

  .rv2-preview-lead,
  .rv2-preview-note {
    margin-top: 10px;
    line-height: 1.5;
  }

  .rv2-preview-list {
    gap: 16px;
    margin: 20px 0;
  }

  .rv2-preview-group li {
    padding: 10px 0;
  }

  .rv2-preview-actions {
    flex-direction: column-reverse;
    gap: 10px;
    margin-top: 12px;
  }

  .rv2-preview-actions .rv2-primary,
  .rv2-preview-actions .rv2-text-btn {
    width: 100%;
    text-align: center;
  }

  .rv2-strip-day {
    min-height: 66px;
    padding: 10px 2px 9px;
    border-radius: 16px;
  }

  .rv2-strip-day b {
    font-size: 18px;
  }

  .rv2-strip-day.is-gap:not(.is-selected):not(.is-today) {
    background: var(--surface);
  }

  .rv2-phone-status {
    margin: 0 2px 18px;
  }

  .rv2-dayboard {
    gap: 14px;
  }

  .rv2-card {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 16px;
    border-radius: 22px;
  }

  .rv2-card.is-work,
  .rv2-card.is-sick,
  .rv2-card.is-holiday,
  .rv2-card.is-off {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 10px 30px rgba(42, 28, 14, 0.04), inset 0 0 0 1px var(--line);
  }

  .rv2-card-who {
    width: 100%;
  }

  .rv2-card-who strong {
    font-size: 16px;
  }

  .rv2-card-who span {
    margin-top: 2px;
  }

  .rv2-card-shift {
    padding: 14px 16px;
  }

  .rv2-face {
    width: 40px;
    height: 40px;
    font-size: 12px;
    line-height: 40px;
  }

  .rv2-sheet {
    max-height: min(92vh, 760px);
    padding: 10px 20px calc(26px + env(safe-area-inset-bottom));
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .rv2-sheet-handle {
    height: 32px;
    margin: 0 0 12px;
  }

  .rv2-choices {
    gap: 10px;
    margin: 20px 0;
  }

  .rv2-choice {
    min-height: 78px;
    padding: 14px;
  }

  .rv2-times {
    gap: 10px;
  }

  .rv2-custom-form {
    padding: 0 14px 16px;
  }

  .rv2-sheet h2 {
    font-size: 26px;
  }
}
</style>

<style>
.main-panel.rv2-surface {
  background: #efe8dc !important;
}

.main-panel.rv2-surface .panel-header-sm {
  background: #14110e !important;
}

.main-panel.rv2-surface .navbar-absolute {
  background: transparent !important;
}

body.rv2-surface {
  background: #efe8dc;
}

@media (max-width: 767px) {
  body.rv2-surface .wrapper {
    height: auto;
    min-height: 100%;
  }

  .main-panel.rv2-surface .panel-header-sm {
    height: 70px;
    padding: 0;
  }

  .main-panel.rv2-surface > .content {
    min-height: 0;
    margin-top: 0;
    padding-bottom: 16px;
  }
}
</style>
